/**
 * 브러시 지우개 알고리즘
 * 원-선분 교차점 보간을 통한 정밀 지우기
 */

// 지우개 반지름 계산
export function getEraserRadius(eraserThickness: number): number {
  return eraserThickness / 2
}

/**
 * 원과 선분의 교차 매개변수(t)를 구한다
 * 선분: P(t) = A + t*(B-A), t ∈ [0, 1]
 * 원: |P(t) - C|² = r²
 * @returns 교차하는 t 값 배열 (0~1 범위, 오름차순)
 */
function circleSegmentIntersections(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  r: number,
): number[] {
  const dx = bx - ax
  const dy = by - ay
  const fx = ax - cx
  const fy = ay - cy

  const a = dx * dx + dy * dy
  const b = 2 * (fx * dx + fy * dy)
  const c = fx * fx + fy * fy - r * r

  let discriminant = b * b - 4 * a * c
  if (discriminant < 0 || a === 0) return []

  discriminant = Math.sqrt(discriminant)
  const results: number[] = []

  const t1 = (-b - discriminant) / (2 * a)
  const t2 = (-b + discriminant) / (2 * a)

  if (t1 >= 0 && t1 <= 1) results.push(t1)
  if (t2 >= 0 && t2 <= 1 && Math.abs(t2 - t1) > 1e-10) results.push(t2)

  return results.sort((a, b) => a - b)
}

// 점이 원 안에 있는지 확인
function isInsideCircle(px: number, py: number, cx: number, cy: number, r: number): boolean {
  return (px - cx) ** 2 + (py - cy) ** 2 <= r * r
}

// 선분 위의 t 위치에서 좌표 보간
function lerp(ax: number, ay: number, bx: number, by: number, t: number): [number, number] {
  return [ax + t * (bx - ax), ay + t * (by - ay)]
}

/**
 * Line의 points 배열에서 지우개 영역을 정밀하게 제거
 * 원-선분 교차점을 계산하여 경계에서 정확히 자름
 *
 * @param points - flat points 배열 [x0, y0, x1, y1, ...]
 * @param eraserX - 지우개 중심 X
 * @param eraserY - 지우개 중심 Y
 * @param eraserRadius - 지우개 반지름
 * @returns 남은 세그먼트 배열들 (빈 배열 = 전체 삭제됨)
 */
export function eraseFromLine(
  points: number[],
  eraserX: number,
  eraserY: number,
  eraserRadius: number,
): number[][] {
  const pointCount = points.length / 2
  if (pointCount < 2) return []

  // 결과 세그먼트들을 수집
  const segments: number[][] = []
  let currentSegment: number[] = []

  for (let i = 0; i < pointCount - 1; i++) {
    const ax = points[i * 2]
    const ay = points[i * 2 + 1]
    const bx = points[(i + 1) * 2]
    const by = points[(i + 1) * 2 + 1]

    const aInside = isInsideCircle(ax, ay, eraserX, eraserY, eraserRadius)
    const bInside = isInsideCircle(bx, by, eraserX, eraserY, eraserRadius)

    const intersections = circleSegmentIntersections(ax, ay, bx, by, eraserX, eraserY, eraserRadius)

    if (!aInside && !bInside && intersections.length === 0) {
      // 세그먼트 전체가 원 밖 → 그대로 유지
      if (currentSegment.length === 0) {
        currentSegment.push(ax, ay)
      }
      currentSegment.push(bx, by)
    } else if (aInside && bInside) {
      // 세그먼트 전체가 원 안 → 삭제
      if (currentSegment.length >= 4) {
        segments.push(currentSegment)
      }
      currentSegment = []
    } else if (!aInside && bInside) {
      // A는 밖, B는 안: A ~ 교차점까지 유지
      if (currentSegment.length === 0) {
        currentSegment.push(ax, ay)
      }
      if (intersections.length > 0) {
        const [ix, iy] = lerp(ax, ay, bx, by, intersections[0])
        currentSegment.push(ix, iy)
      }
      if (currentSegment.length >= 4) {
        segments.push(currentSegment)
      }
      currentSegment = []
    } else if (aInside && !bInside) {
      // A는 안, B는 밖: 교차점 ~ B 유지
      if (currentSegment.length >= 4) {
        segments.push(currentSegment)
      }
      currentSegment = []
      if (intersections.length > 0) {
        const lastT = intersections[intersections.length - 1]
        const [ix, iy] = lerp(ax, ay, bx, by, lastT)
        currentSegment.push(ix, iy)
      }
      currentSegment.push(bx, by)
    } else if (!aInside && !bInside && intersections.length >= 2) {
      // 양쪽 다 밖이지만 중간에 원을 관통
      // A ~ 첫 교차점 유지
      if (currentSegment.length === 0) {
        currentSegment.push(ax, ay)
      }
      const [ix1, iy1] = lerp(ax, ay, bx, by, intersections[0])
      currentSegment.push(ix1, iy1)
      if (currentSegment.length >= 4) {
        segments.push(currentSegment)
      }
      // 두 번째 교차점 ~ B 새 세그먼트 시작
      const [ix2, iy2] = lerp(ax, ay, bx, by, intersections[1])
      currentSegment = [ix2, iy2, bx, by]
    }
  }

  // 마지막 세그먼트 처리
  if (currentSegment.length >= 4) {
    segments.push(currentSegment)
  }

  return segments
}
